/* eslint no-param-reassign: "off" */
import { defineStore } from 'pinia';
import { getApplicantApplications } from '@/services/profile-service';

function getDefaultState() {
  return {
    userId: undefined,
    userPermissions: [],
    // The preferred name to greet the applicant by, or null when their account has none.
    preferredName: null,
    // The `/applicant/applications` summaries, newest first.
    applications: [],
    // 'idle', 'loading', 'ready' or 'error'.
    applicationsState: 'idle',
    // Only ever the ID of one of `applications`.
    lastApplicationId: null,
    // Why the emailed sign-in link didn't sign the applicant in: 'invalid', 'failed' or null.
    signInLinkError: null,
  };
}

const useGlobalStore = defineStore('global', {
  state: () => ({
    ...getDefaultState(),
    isLoggingOut: false,
  }),
  getters: {
    isLoggedIn(state) {
      return Boolean(state.userId);
    },
    applicationIds(state) {
      return state.applications.map(application => application.id);
    },
    // The application to open when the page isn't about one, or null when there is none.
    lastOrNewestApplicationId(state) {
      return state.lastApplicationId ?? state.applications[0]?.id ?? null;
    },
    findApplication(state) {
      return applicationId =>
        state.applications.find(application => application.id === applicationId) ?? null;
    },
    hasApplication() {
      return applicationId => this.findApplication(applicationId) !== null;
    },
  },
  actions: {
    logIn(payload) {
      this.userId = payload.id;
      this.userPermissions = payload.user_permissions ?? [];
      this.preferredName = payload.preferred_name?.trim() || null;
    },
    logOut() {
      Object.assign(this, getDefaultState());
    },
    async loadApplications() {
      this.applicationsState = 'loading';

      try {
        this.applications = await getApplicantApplications();
        this.applicationsState = 'ready';
      } catch {
        this.applicationsState = 'error';
      }
    },
    // Leaves `applicationsState` alone so the layout doesn't show its loading screen; the caller
    // shows its own loading and error state, so a failed request rejects.
    async refreshApplications() {
      this.applications = await getApplicantApplications();
    },
    rememberApplication(applicationId) {
      if (this.hasApplication(applicationId)) {
        this.lastApplicationId = applicationId;
      }
    },
  },
});

export default useGlobalStore;
